/**
 * Chart.js data transformation helpers
 * P0: line, area, bar, hbar, pie, doughnut, radar
 */
import type { ChartVisualType, ChartConfig, ChartBackendData, ChartSeriesData, ChartSeriesConfig, InteractionMode } from '@/types/chart';
import { isMultiSeries as isMultiSeriesFromGroup, isSingleSeries as isSingleSeriesFromGroup } from '@/types/chart';
import { formatNumber } from '@/utils/formatters/number';
import type { Chart, ChartEvent } from 'chart.js';

// ---------------------------------------------------------------------------
// Local types (mirror chart.js shapes, no direct chart.js type imports)
// ---------------------------------------------------------------------------

interface ChartJsDataset {
  label: string;
  data: (number | null)[];
  borderColor?: string;
  backgroundColor?: string | string[];
  fill?: boolean | string;
  pointRadius?: number;
  datalabels?: Record<string, unknown>;
}

/** Extended dataset with type override (for mixed charts like line + bar). */
interface ChartJsMixedDataset extends ChartJsDataset {
  type: string;
}

interface ScaleTitleConfig {
  display?: boolean;
  text?: string;
}

/** Minimal scale config compatible with all chart.js scale types. */
interface ScaleConfig {
  display?: boolean;
  title?: ScaleTitleConfig;
}

interface ChartJsOptions {
  responsive: boolean;
  maintainAspectRatio: boolean;
  indexAxis?: 'x' | 'y';
  plugins?: {
    legend?: {
      display: boolean;
      position?: 'top' | 'bottom' | 'left' | 'right' | 'chartArea';
      onClick?: (e: ChartEvent, legendItem: { datasetIndex?: number }, legend: { chart: Chart }) => void;
    };
    tooltip?: Record<string, unknown>;
    datalabels?: Record<string, unknown>;
  };
  scales?: Record<string, ScaleConfig>;
  interaction?: {
    mode?: InteractionMode;
    intersect?: boolean;
  };
  cutout?: string | number;
  circumference?: number;
  rotation?: number;
}

interface ChartJsData {
  labels: string[];
  datasets: ChartJsDataset[];
}

// ---------------------------------------------------------------------------
// Public helpers
// ---------------------------------------------------------------------------

/**
 * Map ChartVisualType → Chart.js component type string.
 */
const SEGMENT_COLORS = [
  '#3a57e8', '#1aa053', '#c03221', '#f16a1b', '#079aa2',
  '#6c757d', '#e83e8c', '#6610f2', '#fd7e14', '#20c997',
];

/**
 * Apply sqrt or cbrt scale to smooth visual proportions for funnel.
 * Preserves order but reduces extreme differences.
 */
function applyFunnelScale(value: number, scale: 'sqrt' | 'cbrt' | 'log'): number {
  switch (scale) {
    case 'log': return value > 0 ? Math.log10(value) : 0;
    case 'cbrt': return Math.cbrt(value);
    default: return Math.sqrt(value);
  }
}

export function mapChartType(type: ChartVisualType): string {
  const mapping: Record<ChartVisualType, string> = {
    line: 'line',
    area: 'line',    // area = line с fill
    bar: 'bar',
    hbar: 'bar',     // hbar = bar с indexAxis: 'y'
    pie: 'pie',
    doughnut: 'doughnut',
    radar: 'radar',
    funnel: 'funnel',
    radialGauge: 'doughnut', // radialGauge = doughnut-based custom render
  };
  return mapping[type];
}

/**
 * Returns true for cartesian chart types (line, area, bar, hbar).
 */
export function isCartesian(type: ChartVisualType): boolean {
  return ['line', 'area', 'bar', 'hbar'].includes(type);
}

/**
 * Returns true if the chart type supports multiple series.
 * Re-exported from @/types/chart for convenience.
 *
 * @deprecated Use isOverrideAllowed() and isSegmented() and direct type selection instead.
 */
export const isMultiSeries = isMultiSeriesFromGroup;

/**
 * Returns true if the chart type supports only a single series.
 * Re-exported from @/types/chart for convenience.
 *
 * @deprecated Use isOverrideAllowed() and isSegmented() and direct type selection instead.
 */
export const isSingleSeries = isSingleSeriesFromGroup;

/**
 * Convert hex color to rgba string with given alpha.
 */
export function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Build Chart.js options from ChartConfig.
 */
export function buildChartOptions(config: ChartConfig): ChartJsOptions {
  const chartType = config.chartType;

  // Determine effective layout type: if all series share the SAME chartTypeOverride,
  // use it for layout decisions (cutout, scales). Otherwise use panel chartType.
  // This is needed because singleSeries chartType is always 'pie' in the UI,
  // while the actual visual type (doughnut, radialGauge) is set per-series via override.
  const overrideTypes = [...new Set(
    config.series.map((s) => s.chartTypeOverride).filter(Boolean)
  )];
  const effectiveLayoutType = overrideTypes.length === 1
    ? overrideTypes[0]!
    : chartType;

  const options: ChartJsOptions = {
    responsive: true,
    maintainAspectRatio: !isCartesian(chartType),
    plugins: {
      legend: {
        display: config.showLegend === true,
        position: config.legendPosition || 'top',
        onClick: (_e: ChartEvent, legendItem: { datasetIndex?: number }, legend: { chart: Chart }) => {
          const index = legendItem.datasetIndex;
          if (index === undefined) return;

          const chart = legend.chart;
          const meta = chart.getDatasetMeta(index);

          // Toggle visibility: null → true (hide), true/null → null (show)
          (meta as { hidden: boolean | null }).hidden =
            (meta as { hidden: boolean | null }).hidden === null ? true : null;

          chart.update();
        },
      },
      datalabels: { display: false },
    },
  };

  // interaction: apply if configured
  if (config.interactions) {
    options.interaction = {};
    if (config.interactions.mode) {
      options.interaction.mode = config.interactions.mode;
    }
    if (config.interactions.intersect !== undefined) {
      options.interaction.intersect = config.interactions.intersect;
    }
  }

  // hbar: indexAxis 'y' (global chart type OR per-series override)
  if (chartType === 'hbar' || config.series.some((s) => s.chartTypeOverride === 'hbar')) {
    options.indexAxis = 'y';
  }

  // funnel: indexAxis based on orientation (default: vertical → 'y')
  if (chartType === 'funnel' || config.series.some((s) => s.chartTypeOverride === 'funnel')) {
    const orientation = config.funnelOrientation ?? config.series[0]?.funnelOrientation ?? 'vertical';
    options.indexAxis = orientation === 'vertical' ? 'y' : 'x';
  }

  // doughnut: cutout 50%
  if (effectiveLayoutType === 'doughnut') {
    options.cutout = '50%';
  }

  // pie: cutout 0
  if (effectiveLayoutType === 'pie') {
    options.cutout = 0;
  }

  if (chartType === 'funnel') {
    if (!options.plugins) options.plugins = {};
    options.plugins.datalabels = {
      display: true,
      color: '#374151',
    };
  }

  // cartesian: add scales
  if (isCartesian(effectiveLayoutType)) {
    options.scales = {
      x: {
        display: config.showAxes !== false,
        title: { display: !!config.xAxisLabel, text: config.xAxisLabel || '' },
      },
      y: {
        display: config.showAxes !== false,
        title: { display: !!config.yAxisLabel, text: config.yAxisLabel || '' },
      },
    };
  }

  return options;
}

/**
 * Transform backend data (ChartBackendData) into Chart.js-compatible format.
 * Handles mixed charts (line + bar) via per-dataset type override.
 */
export function transformToChartJs(
  data: ChartBackendData,
  config: ChartConfig,
): { chartJsData: ChartJsData; chartJsOptions: ChartJsOptions } {
  const { series, categories } = data;
  // Helper: find config by fieldName, fall back to positional index
  const getSeriesConfig = (s: ChartSeriesData, idx: number): ChartSeriesConfig | undefined => {
    if (s.fieldName) {
      return config.series.find(c => c.fieldName === s.fieldName);
    }
    return config.series[idx];
  };
  const chartType = config.chartType;
  const baseType = mapChartType(chartType);

  // Pre-compute funnel sqrt scaling
  const isFunnelScaled = chartType === 'funnel' && (config.funnelScale === 'sqrt' || config.funnelScale === 'cbrt' || config.funnelScale === 'log');
  let funnelOriginals: (number | null)[][] | null = null;
  if (isFunnelScaled) {
    funnelOriginals = series.map(s => [...s.data]);
  }

  const datasets: (ChartJsDataset | ChartJsMixedDataset)[] = series.map((s: ChartSeriesData, idx: number) => {
    // Effective visual type for this series: override > backend type > global default
    const seriesConfig = getSeriesConfig(s, idx);
    const overrideFromConfig = seriesConfig?.chartTypeOverride;
    const effectiveType: ChartVisualType = overrideFromConfig || s.type || chartType;
    const isRadialSeries = effectiveType === 'pie' || effectiveType === 'doughnut' || effectiveType === 'funnel';
    const isGaugeSeries = effectiveType === 'radialGauge';

    const dataset: ChartJsDataset = {
      label: s.name,
      data: isFunnelScaled ? s.data.map(v => v !== null ? applyFunnelScale(v, config.funnelScale as 'sqrt' | 'cbrt' | 'log') : null) : s.data,
      borderColor: s.color,
      backgroundColor: isRadialSeries
        ? (s.perDataColors && s.perDataColors.length === s.data.length
            ? s.perDataColors
            : s.data.map((_, i) => SEGMENT_COLORS[i % SEGMENT_COLORS.length]))
        : isGaugeSeries
          ? s.color
          : effectiveType === 'area'
            ? hexToRgba(s.color, 0.3)
            : s.color,
    };

    // fill logic: area = line + fill=true
    if (effectiveType === 'area') {
      dataset.fill = true;
      dataset.backgroundColor = hexToRgba(s.color, 0.3);
    } else {
      dataset.fill = false;
    }

    // Point configuration (only for line/area types)
    const isLineOrArea = effectiveType === 'line' || effectiveType === 'area';
    if (isLineOrArea) {
      const showPoints = seriesConfig?.showPoints !== false; // default: true
      dataset.pointRadius = showPoints
        ? Math.max(0, seriesConfig?.pointRadius ?? 3)
        : 0;
    }

    // radialGauge: transform [value] → [value, max-value]
    if (isGaugeSeries && dataset.data.length === 1) {
      const value = dataset.data[0] ?? 0;
      const maxValue = 100;
      dataset.data = [value, Math.max(0, maxValue - value)];
    }

    // Per-dataset Chart.js type override (mixed charts)
    if (overrideFromConfig) {
      const chartJsType = mapChartType(overrideFromConfig);
      if (chartJsType !== baseType) {
        (dataset as ChartJsMixedDataset).type = chartJsType;
      }
    }

    // Per-series data labels
    if (effectiveType === 'funnel') {
      dataset.datalabels = {
        display: true,
        color: config.funnelDataLabelColor ?? '#374151',
        offset: 4,
        font: { size: config.funnelDataLabelFontSize ?? 12, weight: 'bold' as const },
        formatter: (_value: number | null, context: { dataIndex: number; datasetIndex: number; chart: { data: { labels?: unknown[] } } }) => {
          const label = context.chart.data.labels?.[context.dataIndex];
          const originalValue = isFunnelScaled && funnelOriginals
            ? (funnelOriginals[context.datasetIndex]?.[context.dataIndex] ?? _value)
            : _value;
          if (originalValue === null || originalValue === undefined) return '—';
          const formatted = formatNumber(originalValue, {
            useMillions: seriesConfig?.useMillions,
            millionsFrom: seriesConfig?.millionsFrom ?? undefined,
            decimalPlaces: seriesConfig?.decimalPlaces,
          });
          const prefix = seriesConfig?.prefix || '';
          const suffix = seriesConfig?.suffix || '';
          return label ? `${label}: ${prefix}${formatted}${suffix}` : `${prefix}${formatted}${suffix}`;
        },
      };
    } else if (seriesConfig?.showDataLabels) {
      dataset.datalabels = {
        display: true,
        anchor: 'end' as const,
        align: 'top' as const,
        color: '#374151',
        font: { size: 11, weight: '500' as const },
        formatter: (value: number | null) => {
          if (value === null || value === undefined) return '—';
          const formatted = formatNumber(value, {
            useMillions: seriesConfig?.useMillions,
            millionsFrom: seriesConfig?.millionsFrom ?? undefined,
            decimalPlaces: seriesConfig?.decimalPlaces,
          });
          const prefix = seriesConfig?.prefix || '';
          const suffix = seriesConfig?.suffix || '';
          return `${prefix}${formatted}${suffix}`;
        },
      };
    } else {
      dataset.datalabels = { display: false };
    }

    return dataset;
  });

  const chartJsData: ChartJsData = {
    labels: categories,
    datasets,
  };

  const chartJsOptions = buildChartOptions(config);

  if (!chartJsOptions.plugins) {
    chartJsOptions.plugins = {};
  }
  chartJsOptions.plugins.tooltip = {
    callbacks: {
      label: (context: { datasetIndex: number; dataIndex: number; raw: number | null }) => {
        const idx = context.datasetIndex;
        const dataSeries = series[idx];
        if (!dataSeries) return '';
        const sc = dataSeries.fieldName
          ? config.series.find(c => c.fieldName === dataSeries.fieldName)
          : config.series[idx];
        const value = isFunnelScaled && funnelOriginals
          ? (funnelOriginals[idx]?.[context.dataIndex] ?? context.raw)
          : context.raw;
        if (value === null || value === undefined) return '—';
        const raw = formatNumber(value, {
          useMillions: sc?.useMillions,
          millionsFrom: sc?.millionsFrom ?? undefined,
          decimalPlaces: sc?.decimalPlaces,
        });
        const formatted = raw.replace(/\u00A0/g, ' ');
        const prefix = sc?.prefix || '';
        const suffix = sc?.suffix || '';
        return `${prefix}${formatted}${suffix}`;
      },
    },
  };

  return { chartJsData, chartJsOptions };
}
